import React, { useMemo, useRef, useState } from 'react';
import type { DataGridOnCheckedRowsArg } from '@axiom/ui';
import {
  AsyncConnector,
  CondensedHeader,
  DataGrid,
  DataGridHeader,
  Gutter,
  profileStatusColors,
  useApi,
  useClearApiCacheOnUnmount,
} from '@axiom/ui';
import type { Candidate, User } from '@axiom/validation';
import { useNavigate } from 'react-router';

import type {
  readUserTalentResponseType,
  UserBenchFilterType,
} from '../../api/user-talent-api';
import {
  UserBenchTalentSort,
  UserBenchTalentSortKeys,
  UserTalentApi,
} from '../../api/user-talent-api';
import { WindowUtil } from '../../utils/window-util';
import { TaxonomyApi } from '../../api/taxonomy-api';
import { EnvUtil } from '../../utils/env-util';
import { downloadTalentList } from '../../api/legacy-talent-api';
import { encodeApiObjects } from '../../classes/legacy-api-helper';
import { TalentBulkActions } from '../TalentBulkActions/TalentBulkActions';

import {
  lastNameCell,
  flaggedTalentCellRender,
  profileStatusCellRender,
  isPriorityTalentCellRender,
  talentOwnershipCell,
  primaryPracticeAreaCell,
  weeklyAvailabilityCell,
  totalEngagedHoursCell,
  totalReservedHoursCell,
  availabilityPreferencesUpdatedAtCell,
  alphabeticallySmallestEngagementAccountNameCell,
  soonestEngagementEndDateCell,
} from './BenchViewGridCellsUtil';
import {
  flaggedTalentDisplayName,
  isPriorityTalentDisplayName,
  profileStatusFiltersRender,
  talentOwnershipFiltersRender,
} from './BenchViewGridUtil';
import { actionsCell } from './BenchViewGridActionsUtil';

export type BenchViewGridProps = {
  filters: UserBenchFilterType;
  onFilterChanges: (filters?: UserBenchFilterType | null) => void;
  user: User;
};

export const BenchViewGrid = ({
  filters,
  onFilterChanges,
  user,
}: BenchViewGridProps) => {
  const [{ data: taxonomy }] = useApi(TaxonomyApi.readTaxonomy());

  const [resultCount, setResultCount] = useState<number>(0);
  const [checkedRows, setCheckedRows] = useState<DataGridOnCheckedRowsArg>({
    allRowsChecked: false,
    checkedRows: [],
  });
  const checkedRowsRef = useRef(checkedRows);
  checkedRowsRef.current = checkedRows;

  const allLoadedRowsRef = useRef<readUserTalentResponseType['data']>([]);

  const { practiceAreas } = taxonomy;

  const navigate = useNavigate();

  // Clear the endpoint cache on unmount to prevent stale cached requests
  useClearApiCacheOnUnmount(UserTalentApi, `/users/${user.id}/candidates`);

  const gridConnector = useMemo(() => {
    allLoadedRowsRef.current = [];
    return new AsyncConnector<readUserTalentResponseType>({
      getData: args => {
        const newSorts = Object.keys(args.sort) as Array<
          UserBenchFilterType['sort']
        >;
        return UserTalentApi.readUserTalent(
          user.id,
          args.page,
          {
            ...filters,
            sort:
              newSorts.reduce<UserBenchFilterType['sort']>((acc, key) => {
                if (key && args.sort[key] && !acc) {
                  acc = key;
                }

                return acc;
              }, null) ?? undefined,
          },
          Object.values(args.sort)[0] || undefined
        );
      },
      parseData: results => {
        // clear stale data from previous search
        if (results._meta.currentPage === 1) {
          allLoadedRowsRef.current = [];
        }
        allLoadedRowsRef.current = [
          ...allLoadedRowsRef.current,
          ...results.data,
        ];
        setResultCount(results._meta.resultCount);
        if (checkedRowsRef.current.allRowsChecked) {
          setCheckedRows({
            allRowsChecked: true,
            checkedRows: [...allLoadedRowsRef.current],
          });
        }
        return {
          currentPage: results._meta.currentPage,
          totalPages: results._meta.pageCount,
          data: results.data,
        };
      },
      onUnmount: () => {
        UserTalentApi.clearReadUserTalent(user.id);
      },
    });
  }, [user, filters]);

  return (
    <>
      <Gutter vertical="8px">
        <CondensedHeader name="TALENT_COUNT">
          {resultCount} Talent
        </CondensedHeader>
      </Gutter>
      <DataGrid<readUserTalentResponseType>
        name="BENCH_GRID"
        displayMode="infinite"
        dataSource={gridConnector}
        onSelectionChanged={rows => {
          if (rows.length > 0) {
            const rowId = (rows[0]?.id ?? '') as string;
            void navigate(WindowUtil.appendQueryParams(`/bench/${rowId}`));
          }
        }}
        rowsChecked={checkedRows}
        onCheckboxGroupChanged={rows => {
          setCheckedRows(rows);
        }}
        calculateRowBadge={(rowData: Partial<Candidate>) => {
          const status = rowData.profileStatus;
          return status ? profileStatusColors[status].colorCode : '';
        }}
      >
        <DataGridHeader<Candidate>
          name="flaggedTalent"
          pinned="left"
          width={125}
          displayName={flaggedTalentDisplayName}
          cellRender={flaggedTalentCellRender}
        />
        <DataGridHeader<Candidate>
          name="lastName"
          displayName="Talent"
          pinned="left"
          sortingOrder={['asc', 'desc', null]}
          cellRender={lastNameCell}
        />
        <DataGridHeader<Candidate>
          name="profileStatus"
          displayName="Profile status"
          sortingOrder={['asc', 'desc', null]}
          FiltersRender={() =>
            profileStatusFiltersRender({
              filters: { profileStatus: filters.profileStatus ?? undefined },
              onFilterChanges,
            })
          }
          cellRender={profileStatusCellRender}
        />
        <DataGridHeader
          name={UserBenchTalentSortKeys.employmentStatus}
          displayName={
            UserBenchTalentSort[UserBenchTalentSortKeys.employmentStatus]
          }
          sortingOrder={['asc', 'desc', null]}
        />
        <DataGridHeader<Candidate>
          name={UserBenchTalentSortKeys.primaryPracticeArea}
          displayName={
            UserBenchTalentSort[UserBenchTalentSortKeys.primaryPracticeArea]
          }
          sortingOrder={['asc', 'desc', null]}
          cellRender={data =>
            primaryPracticeAreaCell({ data: data.data, practiceAreas })
          }
        />
        <DataGridHeader<Candidate>
          name={UserBenchTalentSortKeys.weeklyAvailability}
          displayName={
            UserBenchTalentSort[UserBenchTalentSortKeys.weeklyAvailability]
          }
          sortingOrder={['asc', 'desc', null]}
          cellRender={weeklyAvailabilityCell}
        />
        <DataGridHeader<Candidate>
          name="totalEngagedHours"
          displayName="Total engaged hrs"
          cellRender={totalEngagedHoursCell}
        />
        <DataGridHeader<Candidate>
          name="totalReservedHours"
          displayName="Reserved hrs"
          cellRender={totalReservedHoursCell}
        />
        <DataGridHeader<Candidate>
          width={250}
          name={UserBenchTalentSortKeys.availabilityPreferencesUpdatedAt}
          displayName={
            UserBenchTalentSort[
              UserBenchTalentSortKeys.availabilityPreferencesUpdatedAt
            ]
          }
          sortingOrder={['asc', 'desc', null]}
          cellRender={availabilityPreferencesUpdatedAtCell}
        />
        <DataGridHeader
          name={UserBenchTalentSortKeys.priority}
          width={184}
          displayName={isPriorityTalentDisplayName}
          sortingOrder={['desc', 'asc', null]}
          cellRender={isPriorityTalentCellRender}
        />
        <DataGridHeader
          name={UserBenchTalentSortKeys.mobilePhone}
          displayName={UserBenchTalentSort[UserBenchTalentSortKeys.mobilePhone]}
          sortingOrder={['asc', 'desc', null]}
        />
        <DataGridHeader
          name={UserBenchTalentSortKeys.personalEmail}
          displayName={
            UserBenchTalentSort[UserBenchTalentSortKeys.personalEmail]
          }
          sortingOrder={['asc', 'desc', null]}
        />
        <DataGridHeader
          name={UserBenchTalentSortKeys.furthestCandidacyStatus}
          displayName={
            UserBenchTalentSort[UserBenchTalentSortKeys.furthestCandidacyStatus]
          }
          sortingOrder={['asc', 'desc', null]}
        />
        <DataGridHeader<Candidate>
          name={
            UserBenchTalentSortKeys.alphabeticallySmallestEngagementAccountName
          }
          displayName={
            UserBenchTalentSort[
              UserBenchTalentSortKeys
                .alphabeticallySmallestEngagementAccountName
            ]
          }
          sortingOrder={['asc', 'desc', null]}
          cellRender={data =>
            alphabeticallySmallestEngagementAccountNameCell({
              data: data.data,
              filters,
            })
          }
        />
        <DataGridHeader<Candidate>
          name={UserBenchTalentSortKeys.soonestEngagementEndDate}
          displayName={
            UserBenchTalentSort[
              UserBenchTalentSortKeys.soonestEngagementEndDate
            ]
          }
          sortingOrder={['asc', 'desc', null]}
          cellRender={data => soonestEngagementEndDateCell({ data: data.data })}
        />
        <DataGridHeader<Candidate>
          name="talent-ownership"
          displayName="Talent ownership"
          cellRender={data =>
            talentOwnershipCell({ data: data.data, filters, userId: user.id })
          }
          FiltersRender={() =>
            talentOwnershipFiltersRender({
              filters: { type: filters.type ?? undefined },
              onFilterChanges,
            })
          }
        />
        <DataGridHeader<Candidate>
          name="actions"
          pinned="right"
          width={200}
          displayName={() => {
            const hasCheckedRows =
              checkedRows.allRowsChecked || checkedRows.checkedRows.length > 0;
            if (!hasCheckedRows) {
              return null;
            }

            const rows =
              checkedRows.allRowsChecked && checkedRows.checkedRows.length === 0
                ? allLoadedRowsRef.current
                : (checkedRows.checkedRows as Candidate[]);

            return (
              <TalentBulkActions
                rows={rows}
                checkedCount={rows.length}
                user={user}
                onExportCsv={async () => {
                  const search = {
                    request: {
                      filters: checkedRows.allRowsChecked
                        ? { ...filters, ownerUserId: [user.id] }
                        : { ids: rows.map(row => row.id) },
                    },
                  };
                  await downloadTalentList(search);

                  window.location.href = `${
                    EnvUtil.apiEnvoyUrl
                  }/downloads/candidates?${encodeApiObjects(search)}`;
                }}
              />
            );
          }}
          cellRender={data => actionsCell({ data: data.data, user })}
        />
      </DataGrid>
    </>
  );
};
